/**
 * アプリケーションルートコンポーネント
 *
 * @file App.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * Reactアプリケーションのルートコンポーネント。Material UIのThemeProvider、
 * エラーバウンダリー、ルーティングの初期設定を行う。
 */
import React, { useEffect } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/useAuth';
import { CartProvider } from './contexts/CartContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { NotificationProvider } from './components/common/NotificationSystem';
import { SideMenuProvider } from './contexts/SideMenuContext';
import './config/axios';
import { fetchCsrfToken } from './utils/csrf';

interface AppContentProps {
  children: React.ReactNode;
}

const AppContent: React.FC<AppContentProps> = ({ children }) => {
  const { isLoading, isAuthenticated, user } = useAuth();

  // デバッグログ: AppContent状態確認
  console.log('[AppContent] isLoading:', isLoading, '| isAuthenticated:', isAuthenticated, '| user:', user);

  console.log('[AppContent] Auth initialized, rendering children');
  return (
    <div>
      {children}
    </div>
  );
};

interface AppProps {
  children: React.ReactNode;
  role: 'buyer' | 'seller' | 'admin';
  notificationDurationSeconds: number;
}

const App: React.FC<AppProps> = ({ children, role, notificationDurationSeconds }) => {
  if (!role) {
    throw new Error('[App] role prop is required and must be one of: buyer, seller, admin');
  }

  // アプリケーション起動時にCSRFトークンを取得
  useEffect(() => {
    fetchCsrfToken(role).catch((error: unknown) => {
      console.error('初期CSRFトークン取得エラー:', error);
    });
  }, [role]);

  return (
    <LanguageProvider>
      <SideMenuProvider>
        <NotificationProvider options={{ defaultDuration: notificationDurationSeconds * 1000 }}>
          <AuthProvider>
            <CartProvider>
              <FavoritesProvider>
                <AppContent>{children}</AppContent>
              </FavoritesProvider>
            </CartProvider>
          </AuthProvider>
        </NotificationProvider>
      </SideMenuProvider>
    </LanguageProvider>
  );
};

export default App;
